import React, { useState } from "react";
import { ref, get, update, query, orderByChild, equalTo } from "firebase/database";
import { db } from "../services/firebase";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaLock,
  FaShieldAlt,
  FaUserCheck,
  FaIdBadge,
  FaKey,
  FaCamera,
  FaLightbulb,
  FaWindowClose,
  FaIdCard
} from "react-icons/fa";
import "./StudentLogin.css";

export default function StudentLogin() {
  const [studentId, setStudentId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    if (/windows/i.test(ua)) return "Windows PC";
    if (/macintosh/i.test(ua)) return "Mac";
    if (/iphone|ipad|ipod/i.test(ua)) return "iOS Device";
    if (/android/i.test(ua)) return "Android Device";
    return "Unknown Device";
  };

  const getIP = async () => {
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      return data.ip;
    } catch {
      return "Unknown IP";
    }
  };

  const handleLogin = async () => {
    if (!studentId || !password) {
      toast.error("Please enter Student ID and Access Code");
      return;
    }

    setLoading(true);

    try {
      const credRef = ref(db, `studentCredentials/${studentId}`);
      const snap = await get(credRef);

      if (!snap.exists()) {
        toast.error("Invalid Student ID");
        setLoading(false);
        return;
      }

      const data = snap.val();
      const now = Date.now();

      if (data.password !== password) {
        toast.error("Incorrect Access Code");
        setLoading(false);
        return;
      }

      // Check if student wants to view result (already submitted)
      if (data.used) {
        // Try to find result by checking studentAttempts
        const attemptRef = ref(db, `studentAttempts`);
        const attemptSnap = await get(attemptRef);
        let specialStudentId = null;
        
        if (attemptSnap.exists()) {
          const attempts = attemptSnap.val();
          const attempt = Object.values(attempts).find(
            (a) => a.loginId === studentId
          );
          if (attempt) {
            specialStudentId = attempt.specialStudentId;
          }
        }
        
        // If result exists, allow viewing
        if (specialStudentId) {
          const resultRef = ref(db, `studentResults/${specialStudentId}`);
          const resultSnap = await get(resultRef);
          
          if (resultSnap.exists()) {
            // Store session for result viewing
            localStorage.setItem(
              "studentSession",
              JSON.stringify({
                studentId,
                testId: data.testId || "DEFAULT",
                testType: data.testType,
                institute: data.institute,
                year: data.year,
                specialStudentId,
                viewingResult: true
              })
            );
            
            localStorage.setItem(
              "activeTest",
              JSON.stringify({
                specialStudentId,
                testId: data.testId || "DEFAULT"
              })
            );
            
            toast.success("Redirecting to your result...");
            setTimeout(() => navigate("/result"), 1000);
            setLoading(false);
            return;
          }
        }
        
        toast.error("This ID has already been used. Result not available yet.");
        setLoading(false);
        return;
      }

      // Check expiry using expiresAt timestamp or endTime
      const expiresAt = data.expiresAt || new Date(data.endTime).getTime();
      if (now > expiresAt) {
        toast.error("Exam has expired");
        await update(credRef, { expired: true, status: "EXPIRED" });
        setLoading(false);
        return;
      }

      const start = new Date(data.startTime).getTime();
      if (now < start) {
        toast.warning(`Exam starts at ${new Date(data.startTime).toLocaleString()}`);
        setLoading(false);
        return;
      }

      const device = getDeviceInfo();
      const ip = await getIP();

      // Mark as used immediately (one-time use)
      await update(credRef, {
        used: true,
        usedAt: now,
        status: "IN_USE",
        "session/active": true,
        "session/device": device,
        "session/ip": ip,
        "session/lastLogin": now
      });

      // Store session with testId (DEFAULT or teacher's test)
      localStorage.setItem(
        "studentSession",
        JSON.stringify({
          studentId,
          testId: data.testId || "DEFAULT", // Link to test
          testType: data.testType,
          institute: data.institute,
          year: data.year,
          device,
          ip,
          loginTime: now
        })
      );

      toast.success("Login successful. Redirecting...");
      setTimeout(() => navigate("/student-details"), 1500);
    } catch (err) {
      toast.error("Server error. Please try again.");
    }

    setLoading(false);
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      <div className="studentlogin-page">
  {/* LEFT PANEL */}
  <div className="studentlogin-left">
    <div className="studentlogin-brand">
      <div className="studentlogin-brand-logo">B</div>
      <div className="studentlogin-brand-text">
        <h2>BodhAI</h2>
        <span>AI Engine — New Era</span>
      </div>
    </div>

    <div className="studentlogin-hero">
      <h1>Ready for Your Exam?</h1>
      <p>
        Log in using your student credentials to access your scheduled
        examination. Please ensure you are in a quiet, well-lit environment
        before starting.
      </p>
    </div>

    <div className="studentlogin-badges">
      <div className="studentlogin-badge-card">
        <FaLock />
        <span>Secure Login</span>
      </div>

      <div className="studentlogin-badge-card">
        <FaShieldAlt />
        <span>AI Proctored</span>
      </div>

      <div className="studentlogin-badge-card">
        <FaUserCheck />
        <span>Privacy First</span>
      </div>
    </div>
  </div>

  {/* RIGHT PANEL */}
  <div className="studentlogin-right">
    <div className="studentlogin-card">
      <h2>Student Login</h2>
      <p className="studentlogin-subtitle">
        Enter your credentials to start your exam
      </p>

      <label className="studentlogin-label">
        <FaIdBadge /> Student ID / Roll Number
      </label>
      <input
        className="studentlogin-input"
        type="text"
        placeholder="e.g. STU2024001"
        value={studentId}
        onChange={(e) => setStudentId(e.target.value.toUpperCase())}
      />

      <label className="studentlogin-label">
        <FaKey /> Exam Access Code
      </label>
      <input
        className="studentlogin-input"
        type="password"
        placeholder="Enter your access code"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <button
        className="studentlogin-button"
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? "Verifying..." : "Start Exam"}
      </button>

      <div className="studentlogin-info-box">
        <strong>Before You Begin:</strong>
        <ul>
          <li><FaCamera /> Ensure your camera is working</li>
          <li><FaLightbulb /> Quiet, well-lit space</li>
          <li><FaWindowClose /> Close other browser tabs</li>
          <li><FaIdCard /> Keep your ID ready</li>
        </ul>
      </div>

      <div className="studentlogin-teacher-link">
        Are you a teacher? <Link to="/teacher-login">Login here</Link>
      </div>

      <div className="studentlogin-check-result" style={{ marginTop: "16px", textAlign: "center" }}>
        <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "8px" }}>
          Already submitted your exam?
        </p>
        <button
          onClick={() => {
            // Check if student has result in localStorage
            const activeTest = JSON.parse(localStorage.getItem("activeTest") || "{}");
            const studentSession = JSON.parse(localStorage.getItem("studentSession") || "{}");
            if (activeTest.specialStudentId || studentSession.specialStudentId) {
              navigate("/result");
            } else {
              toast.info("Please login first to view your result");
            }
          }}
          style={{
            padding: "8px 16px",
            fontSize: "13px",
            background: "transparent",
            color: "#667eea",
            border: "1px solid #667eea",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600"
          }}
        >
          Check My Result
        </button>
      </div>
    </div>
  </div>
</div>

    </>
  );
}
